/* eslint-disable @next/next/no-img-element */
import { useEffect, useState, Fragment } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

import { apiGetTarrantRegistrationTopics } from "../../lib/api/speakers";

import { Transition, Listbox } from "@headlessui/react";
import {
  ExclamationCircleIcon,
  SelectorIcon,
  CheckIcon,
} from "@heroicons/react/outline";

import { IoMdClose } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";

const positions = ["FOR", "AGAINST", "UNDECIDED", "NO STANCE"];

const TarrantCountyRegister = ({
  saveSpeaker,
  topic,
  tarrantSuccess,
  meeting,
}) => {
  const [canSave, setCanSave] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    addressPrimary: "",
    addressSecondary: "",
    city: "",
    state: "TX",
    zipCode: "",
    attending: true,
    speaking: true,
    comment: "",
    representingOrg: "",
    isResident: true,
    subject: "",
    position: positions[0],
  });
  const [availTopics, setAvailTopics] = useState([]);
  const [topicSearch, setTopicSearch] = useState("");
  const [topicDropdownOpen, setTopicDropdownOpen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);

  useEffect(() => {
    async function fetchTopics(group) {
      const result = await apiGetTarrantRegistrationTopics(group, meeting);
      if (result) {
        setAvailTopics(result);
      }
    }
    if (topic) {
      fetchTopics(topic.agendaGroup._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  useEffect(() => {
    const requiredFields = [
      "firstName",
      "lastName",
      "phoneNumber",
      "addressPrimary",
      "city",
      "state",
      "zipCode",
    ];

    const isValid = requiredFields.every((field) => {
      const value = user[field];
      return typeof value === "string" && value.trim() !== "";
    });

    setCanSave(isValid);
  }, [user]);

  function change(key, value) {
    setUser({
      ...user,
      [key]: value,
    });
  }

  function handlePosition(value) {
    setUser({
      ...user,
      position: value,
    });
  }

  function handleSubmit() {
    const groupName = topic?.agendaGroup?.parentGroup
      ? `${topic.agendaGroup.parentGroup.name} - ${topic.agendaGroup.name}`
      : topic?.agendaGroup?.name ?? "";

    const { firstName, lastName, ...rest } = user;
    const fullName = `${firstName} ${lastName}`.trim();

    saveSpeaker(
      "submit",
      { ...rest, name: fullName },
      {
        _id: topic._id,
        verbiage: topic?.title,
      },
      selectedTopics
    );

    setUser({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      addressPrimary: "",
      addressSecondary: "",
      city: "",
      state: "TX",
      zipCode: "",
      attending: true,
      speaking: true,
      comment: "",
      representingOrg: "",
      isResident: true,
      subject: "",
      position: positions[0],
    });
  }

  function toggleTopic(topic) {
    setSelectedTopics((prev) =>
      prev.some((t) => t.id === topic.id)
        ? prev.filter((t) => t.id !== topic.id)
        : [...prev, topic]
    );
    setTopicSearch("");
    setTopicDropdownOpen(false);
  }

  function removeTopic(id) {
    setSelectedTopics((prev) => prev.filter((t) => t.id !== id));
  }

  const filteredTopics = availTopics.filter(
    (t) =>
      !selectedTopics.some((s) => s.id === t.id) &&
      t.title.toLowerCase().includes(topicSearch.toLowerCase())
  );

  const isFieldInvalid = (field) => {
    return !user[field] || user[field].trim?.() === "";
  };

  return (
    <motion.div
      className="h-screen fixed inset-0 bg-white z-50"
      initial={{ x: "100%" }}
      animate={{ x: "0%" }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between border-b border-slate-400 p-4">
        <div className="flex">
          <p className="text-black font-bold ml-4 py-1.5 text-sm lg:text-base 2xl:text-xl">
            Tarrant County Commissioners Court
          </p>
        </div>
        <IoMdClose
          className="h-7 w-7 text-black cursor-pointer hover:text-slate-500 hover:rotate-180 transform duration-300"
          onClick={() => {
            saveSpeaker("cancel");
            setUser({
              name: "",
              email: "",
              phoneNumber: "",
              addressPrimary: "",
              addressSecondary: "",
              city: "",
              state: "TX",
              zipCode: "",
              attending: true,
              speaking: true,
              comment: "",
              representingOrg: "",
              isResident: true,
              subject: "",
              position: positions[0],
            });
          }}
        />
      </div>
      <div className="overflow-y-auto max-h-[calc(100vh-64px)] pb-32">
        {tarrantSuccess ? (
          <div className="w-full flex flex-col items-center mt-4">
            <div className="flex items-center">
              <FaCheckCircle className="h-5 w-5 md:h-7 md:w-7 2xl:h-8 2xl:w-8 text-blue-500 mr-4" />
              <p className="text-sm lg:text-base 2xl:text-xl font-semibold">
                Your Registration has been received.
              </p>
            </div>

            <p className="text-sm lg:text-sm 2xl:text-base">
              You will receive a confirmation email shortly.
            </p>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center mt-4">
            <p className="text-black font-semibold 2xl:text-lg underline">
              Public Comment Form
            </p>
            <p className="text-black font-semibold 2xl:text-lg mt-1">
              {topic?.agendaGroup.parentGroup
                ? `${topic?.agendaGroup.parentGroup.name} - ${topic?.agendaGroup.name}`
                : topic?.agendaGroup.name}
            </p>
            {availTopics.length > 0 && (
              <div className="w-full max-w-7xl 2xl:max-w-5xl mx-auto px-4 mt-3">
                <p className="text-black font-semibold text-sm 2xl:text-base mb-2">
                  Agenda Item(s)
                </p>

                {/* Selected tags */}
                {selectedTopics.length > 0 && (
                  <div
                    className="flex flex-wrap gap-2 mb-2"
                    role="list"
                    aria-label="Selected agenda items"
                  >
                    {selectedTopics.map((t) => (
                      <span
                        key={t.id}
                        role="listitem"
                        className="inline-flex items-center bg-blue-100 border border-blue-300 text-black text-xs 2xl:text-sm font-medium px-2.5 py-1 rounded-md max-w-[200px] sm:max-w-xs"
                      >
                        <span className="flex flex-col min-w-0">
                          <span className="truncate text-gray-700 text-[10px] font-semibold uppercase tracking-wide">
                            {t.agendaGroupName}
                          </span>
                          <span className="truncate text-black">{t.title}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeTopic(t.id)}
                          aria-label={`Remove ${t.title}`}
                          className="ml-1.5 shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 hover:text-red-600 text-gray-600"
                        >
                          <IoMdClose
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search or click to see agenda items..."
                    value={topicSearch}
                    onChange={(e) => {
                      setTopicSearch(e.target.value);
                      setTopicDropdownOpen(true);
                    }}
                    onFocus={() => setTopicDropdownOpen(true)}
                    onBlur={() =>
                      setTimeout(() => setTopicDropdownOpen(false), 150)
                    }
                    className="w-full border border-black rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />

                  {/* Dropdown */}
                  {topicDropdownOpen && filteredTopics.length > 0 && (
                    <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredTopics.map((t) => (
                        <li
                          key={t.id}
                          onMouseDown={() => toggleTopic(t)}
                          className="px-3 py-2 text-sm text-black hover:bg-blue-50 hover:text-blue-700 cursor-pointer"
                        >
                          <span className="block text-xs text-gray-500">
                            {t.agendaGroupName}
                          </span>
                          <span className="block">{t.title}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {topicDropdownOpen &&
                    topicSearch &&
                    filteredTopics.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">
                        No matching items found.
                      </div>
                    )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 lg:gap-4 p-4 max-w-7xl 2xl:max-w-5xl mx-auto w-full">
              {/* FirstName */}
              <div className="">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  First Name{" "}
                  {isFieldInvalid("firstName") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="firstName"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.firstName}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Last Name */}
              <div className="">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  Last Name{" "}
                  {isFieldInvalid("lastName") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="lastName"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.lastName}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Email */}
              <div className="md:col-span-2">
                <label
                  htmlFor="full_name"
                  className="text-sm font-medium text-black inline-flex items-center"
                >
                  Email{" "}
                  {isFieldInvalid("email") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="full_name"
                    id="email"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.email}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Phone Number */}
              <div className="">
                <label
                  htmlFor="title"
                  className="inline-flex text-sm font-medium text-black"
                >
                  Phone Number{" "}
                  {isFieldInvalid("phoneNumber") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="phoneNumber"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.phoneNumber}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Address */}
              <div className="md:col-span-2">
                <label
                  htmlFor="title"
                  className="text-sm font-medium text-black inline-flex"
                >
                  Address{" "}
                  {isFieldInvalid("addressPrimary") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="addressPrimary"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.addressPrimary}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* City */}
              <div className="md:col-span-2">
                <label
                  htmlFor="title"
                  className="text-sm font-medium text-black inline-flex"
                >
                  City{" "}
                  {isFieldInvalid("city") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="city"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.city}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* State */}
              {/* Zip Code */}
              <div className="">
                <label
                  htmlFor="title"
                  className="inline-flex text-sm font-medium text-black"
                >
                  Zip Code{" "}
                  {isFieldInvalid("zipCode") && (
                    <ExclamationCircleIcon
                      className="h-4 w-4 text-red-700 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="title"
                    id="zipCode"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.zipCode}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  />
                </div>
              </div>
              {/* Phone Number */}

              {/* Position */}
              <div className="md:col-span-5">
                <label
                  htmlFor="position"
                  className="block text-sm font-medium text-black"
                >
                  Position
                </label>
                <Listbox value={user.position} onChange={handlePosition}>
                  {({ open }) => (
                    <>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-md border-2 border-coolGray-400 shadow-md cursor-default focus:outline-none sm:text-sm">
                          <span className="block truncate text-black">
                            {user.position}
                          </span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <SelectorIcon
                              className="w-5 h-5 text-black p-0.5"
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>
                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options
                            static
                            className="absolute w-full py-1 mt-1 overflow-auto text-base bg-zinc-50 rounded-md shadow-2xl max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 border-l border-r border-b border-gray-600"
                          >
                            {positions.map((person, personIdx) => (
                              <Listbox.Option
                                key={personIdx}
                                className={({ active }) =>
                                  `${
                                    active
                                      ? "text-white bg-coolGray-600"
                                      : "text-gray-900"
                                  }
                                    cursor-default select-none relative py-2 pl-10 pr-4`
                                }
                                value={person}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span
                                      className={`${
                                        selected ? "font-medium" : "font-normal"
                                      } block truncate`}
                                    >
                                      {person}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={`${
                                          active
                                            ? "text-white"
                                            : "text-gray-900"
                                        }
                                          absolute inset-y-0 left-0 flex items-center pl-3`}
                                      >
                                        <CheckIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </>
                  )}
                </Listbox>
              </div>
              {/* CHeckboxes */}
              {/* <div className="md:col-span-5 max-w-7xl 2xl:max-w-3xl mx-auto">
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    checked={user.speaking}
                    className="h-4 w-4 md:h-7 md:w-7 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => change("speaking", true)}
                  />
                  <p className="text-sm md:text-base 2xl:text-xl font-medium">
                    I wish to address the Court on the following agenda item.
                  </p>
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    checked={!user.speaking}
                    className="h-4 w-4 md:h-7 md:w-7 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => change("speaking", false)}
                  />
                  <p className="text-sm md:tex-tbase 2xl:text-xl font-medium">
                    I do not wish to address the Court, but I would like to
                    submit the following opinion.
                  </p>
                </div>
              </div> */}
              {/* Comments */}
              <div className="md:col-span-5">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-black"
                >
                  Comments
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <textarea
                    type="text"
                    name="comment"
                    id="comment"
                    rows="4"
                    className="mt-1 block w-full border border-black rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm"
                    value={user.comment}
                    onChange={(e) => {
                      //   if (!signage) {
                      change(e.target.id, e.target.value);
                      //   }
                    }}
                  ></textarea>
                </div>
              </div>
              {/* GuideLInes */}
              <p className="text-black font-semibold text-sm lg:text-base 2xl:text-lg mt-2 md:col-span-5 text-center">
                GUIDELINE FOR SPEAKERS
              </p>
              <div className="p-4 md:col-span-5">
                <ul className="list-disc pl-5 space-y-2 text-black text-sm lg:text-base">
                  <li>
                    Speaker order may be on a first-come, first-served basis
                    (using the date and time the public comment form is
                    received). However, speaker order is wholly up to the
                    discretion of the County Judge. The County Judge may allow
                    public commentors who are residents of Tarrant County to
                    speak prior to commentors who are not Tarrant County
                    residents.
                  </li>
                  <li>
                    Speakers will be called to speak by the County Judge or
                    presiding officer.
                  </li>
                  <li>
                    A member of the public addressing the Court during regular
                    and special meetings shall do so only from the podium.
                  </li>
                  <li>
                    Before addressing the Court, speakers may be asked their
                    name, address or Commissioner precinct number, and the
                    agenda item that they will be addressing, if any.
                  </li>
                  <li>
                    Speakers shall stop speaking when the allocated time to
                    speak has expired.
                  </li>
                  <li>
                    The County Judge maintains the right to interrupt and/or
                    excuse a speaker when the allocated time has expired, if the
                    speaker otherwise violates these guidelines, or for any
                    other matter at the discretion of the County Judge.
                  </li>
                </ul>
              </div>
              <p className="text-black text-justify 2xl:text-lg mt-2 md:col-span-5">
                Everyone attending a regular or special meeting of Commissioners
                Court must adhere to these general rules of decorum, so each
                meeting is conducted in a timely and orderly manner.
              </p>
              {/* Prohibited Behaviour */}
              <p className="text-black font-semibold text-sm lg:text-base 2xl:text-lg mt-2 md:col-span-5">
                Prohibited Behavior
              </p>
              <div className="p-4 md:col-span-5">
                <ul className="list-disc pl-5 space-y-2 text-black text-sm lg:text-base">
                  <li>
                    Personal attacks, impertinent, profane, obscene, or
                    slanderous remarks.
                  </li>
                  <li>Threatening, disruptive, or unruly behavior.</li>
                  <li>
                    Disruptive remarks or actions of approval or disapproval
                    from the audience, such as feet stomping, snapping,
                    whistling, yelling, clapping, verbal affirmation or
                    rejection, or similar distractions, except that orderly
                    clapping during the Proclamations, Resolutions, and
                    Presentations section of the agenda is permissible.
                  </li>
                  <li>
                    Approaching the dais on which the members of the
                    Commissioners Court sit beyond the speaker&#39;s podium or
                    to the immediate left or right of the podium, unless by the
                    invitation of a member of the Commissioners Court.
                  </li>
                  <li>
                    Wearing or displaying signs, flags, banners, props,
                    placards, or similar items larger than 8 ½ by 11 inches.
                    This includes items that can be illuminated or be attached
                    to any pole, stick, or other device. This does not include
                    words or symbols on personal apparel, such as shirts, hats,
                    and other standard items of clothing.
                  </li>
                  <li>
                    The County Judge maintains the right to interrupt and/or
                    excuse a speaker when the allocated time has expired, if the
                    speaker otherwise violates these guidelines, or for any
                    other matter at the discretion of the County Judge.
                  </li>
                </ul>
              </div>
              <p className="text-black font-semibold text-sm lg:text-base 2xl:text-lg mt-2 md:col-span-5">
                Cell Phones and Electronic Devices
              </p>
              <p className="text-black text-justify 2xl:text-lg mt-2 md:col-span-5">
                All cellular phones, electronic devices, or other communication
                devices are to be on “silent” mode at all times while
                Commissioners Court is in session.
              </p>
              <p className="text-black font-semibold text-sm lg:text-base 2xl:text-lg mt-2 md:col-span-5">
                Failure to Comply
              </p>
              <p className="text-black text-justify 2xl:text-lg mt-2 md:col-span-5">
                Failure to comply with any part of the Tarrant County
                Commissioners Court Rules of Decorum may result in sanctions,
                including but not limited to, the following:
              </p>
              <div className="p-4 md:col-span-5">
                <ul className="list-disc pl-5 space-y-2 text-black text-sm lg:text-base">
                  <li>
                    removal from the Court by the County Judge or presiding
                    officer;
                  </li>
                  <li>
                    temporary prohibition from commenting in person during
                    Court;
                  </li>
                  <li>cancellation of a speaker&#39;s remaining time;</li>
                  <li>
                    a finding of contempt of Commissioners Court pursuant to
                    Texas Local Government Code Section 81.023 which is
                    punishable by a fine of not more than twenty-five (25)
                    dollars or by confinement of not more than twenty-four (24)
                    hours;
                  </li>
                  <li>
                    referral of matter for other civil sanctions and/or criminal
                    charges as may be authorized under the Constitution and laws
                    of the State of Texas.
                  </li>
                </ul>
              </div>
              <div className="md:col-span-5">
                <p className="text-red-700 font-semibold text-justify 2xl:text-lg mt-2">
                  In submitting this Audience Participation Form you agree to
                  read and abide by the{" "}
                  <a
                    href="http://www.tarrantcountytx.gov/rulesofdecorum"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-800 underline hover:text-blue-800"
                  >
                    Tarrant County Rules of Decorum Policy
                  </a>
                  .
                </p>
              </div>
              {canSave && (
                <div className="flex items-center space-x-2 md:col-span-5">
                  <input
                    type="checkbox"
                    checked={canSubmit}
                    className="h-4 w-4 2xl:h-6 2xl:w-6 text-zinc-800 bg-white border-2 border-black rounded focus:outline-none"
                    onClick={() => setCanSubmit(!canSubmit)}
                  />
                  <p className="text-sm 2xl:text-xl font-medium">
                    I have read and agree to abide by the Tarrant County
                    Commissioners Court Rules of Decorum.
                  </p>
                </div>
              )}
              {canSave && (
                <div className="md:col-span-5 flex justify-between">
                  <button
                    className={clsx(
                      canSubmit
                        ? "bg-blue-600"
                        : "bg-gray-400 pointer-events-none",
                      "px-4 py-2 rounded-lg text-white font-semibold hover:bg-blue-400 transform duration-200"
                    )}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TarrantCountyRegister;
